/**
 * The shapes of a client's messaging services, as the API sends them.
 *
 * Nothing here names a vendor. What a provider needs — its credential fields,
 * its settings, its template fields, what it can do — arrives in the catalog, so
 * a provider added to the API appears in the Services tab with no change here.
 */

import { api } from '@connect/web-shared';

export type FieldKind = 'text' | 'secret' | 'select' | 'number' | 'url';

export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  required: boolean;
  help: string | null;
  placeholder: string | null;
  options: { value: string; label: string }[];
  pattern: string | null;
  max_length: number | null;
  default: string | null;
}

export type Capability =
  | 'verify'
  | 'balance'
  | 'sync_senders'
  | 'sync_templates'
  | 'send'
  | 'bulk_send'
  | 'delivery_reports'
  | 'delivery_webhooks'
  | 'conversations'
  | 'create_templates';

export interface ProviderDef {
  id: string;
  label: string;
  channel: string;
  description: string;
  credentials: FieldSpec[];
  settings: FieldSpec[];
  template_attributes: FieldSpec[];
  template_categories: string[];
  /** The encodings a template may be sent in; the first is the default. */
  template_encodings: { value: string; label: string }[];
  capabilities: Capability[];
  /** False where templates come only by sync: the vendor defines them (WhatsApp). */
  manual_templates: boolean;
  /** A template carries a design round its text — RCS cards and suggestions. */
  rich_templates: boolean;
  /** Connect bills what it sends from the client's prepaid wallet (RCS). */
  prepaid_wallet: boolean;
  /**
   * The credential naming the vendor account (a WhatsApp Business Account id).
   * Set: a client may connect several accounts of this provider, each named.
   */
  account_field?: string | null;
  /** The slug of this provider's setup guide in the docs, if it has one. */
  guide: string | null;
}

export interface ChannelDef {
  id: string;
  label: string;
  description: string;
  sender_label: string;
  template_label: string;
  /** The longest value one slot takes: DLT's 30 for SMS. */
  max_variable_length: number;
  /** Whether a value may span lines (WhatsApp: no). */
  line_breaks_in_values: boolean;
  /** Billed in parts by length (SMS), or one message whatever its length. */
  counts_parts: boolean;
  /** Whether a send chooses its category, or the template's registration fixed it. */
  category_per_send: boolean;
  providers: ProviderDef[];
}

export interface CredentialState {
  set: boolean;
  value?: string | null;
}

export interface ClientChannel {
  public_id: string;
  client_public_id: string;
  channel: string;
  channel_label: string;
  provider: string;
  provider_label: string;
  label: string | null;
  status: 'active' | 'paused';
  is_primary: boolean;
  capabilities: Capability[];
  credentials: Record<string, CredentialState>;
  settings: Record<string, string>;
  verified_at: string | null;
  last_error: string | null;
  account_status: {
    balance: string | null;
    currency: string | null;
    /** Whatever else the provider said, labelled: a WhatsApp number's name and quality. */
    details?: Record<string, string> | null;
  } | null;
  /** Below this balance the client and staff are told; null is off. */
  low_balance_threshold: string | null;
  balance_checked_at: string | null;
  low_balance: boolean;
  /** Where a provider that pushes delivery reports sends them, for pasting at the provider. */
  webhook: { path: string; url: string | null; verify_token: string } | null;
  sender_count: number;
  template_count: number;
  templates_ready: number;
  /** Where it sits at the vendor, for a provider Connect holds an account at (`docs/31`). */
  vendor?: {
    account: { public_id: string; label: string } | null;
    customer_ref: string | null;
    profile: Record<string, string>;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface Sender {
  public_id: string;
  sender_id: string;
  /** How a person knows it where the id is not that: a WhatsApp number's display. */
  label: string | null;
  status: string;
  is_default: boolean;
  synced_at: string | null;
  created_at: string;
}

export interface SenderSyncResult {
  created: number;
  updated: number;
  not_at_provider: string[];
  senders: Sender[];
}

/**
 * Where a slot's value comes from: `form` is typed once when sending and is the
 * same for every recipient; `contact` is read from each recipient.
 */
export type VariableSource = 'form' | 'contact';

export interface TemplateVariable {
  source: VariableSource;
  key: string;
  label: string;
  /** Its value is masked everywhere after sending, on every send (docs/43 §4). */
  sensitive?: boolean;
}

export interface VariableCatalog {
  sources: { id: VariableSource; label: string; description: string }[];
  contact_fields: { key: string; label: string }[];
}

export interface ChannelTemplate {
  public_id: string;
  name: string;
  provider_template_id: string;
  category: string | null;
  body: string;
  attributes: Record<string, string>;
  /** One per {#var#} slot, in order; `null` is a slot not set up yet. */
  variables: (TemplateVariable | null)[];
  variable_count: number;
  /** What a send asks for once, for every recipient. */
  form_fields: { key: string; label: string }[];
  is_ready: boolean;
  /** One of the provider's `template_encodings`, or null where it has none. */
  encoding: string | null;
  is_active: boolean;
  /** The header the provider asks for with every send — WhatsApp's image,
   * video, document or place — or null where there is none to set. */
  header_needed: TemplateHeaderFormat | null;
  header: TemplateHeader | null;
  /** The RCS design round the text (`rcs-design.ts`), or null. */
  rich: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export type TemplateHeaderFormat = 'image' | 'video' | 'document' | 'location';

/** What a template shows at the top, as the API describes it — never where it is kept. */
export interface TemplateHeader {
  format: TemplateHeaderFormat;
  filename: string | null;
  mime: string | null;
  size: number | null;
  latitude: number | null;
  longitude: number | null;
  name: string | null;
  address: string | null;
}

export interface TemplateSyncResult {
  created: number;
  updated: number;
  /** Held here but no longer listed by the provider, by name. */
  not_at_provider: string[];
  templates: ChannelTemplate[];
}

/** The query keys, in one place so a mutation invalidates what it changed. */
export const messagingKeys = {
  catalog: ['messaging', 'catalog'] as const,
  channels: (client: string) => ['messaging', 'client', client, 'channels'] as const,
  senders: (channel: string) => ['messaging', 'channel', channel, 'senders'] as const,
  templates: (channel: string) => ['messaging', 'channel', channel, 'templates'] as const,
  variableCatalog: ['messaging', 'template-variables'] as const,
  suppressions: (client: string) => ['messaging', 'client', client, 'suppressions'] as const,
  sending: (client: string) => ['messaging', 'client', client, 'sending'] as const,
  reports: (client: string) => ['messaging', 'client', client, 'reports'] as const,
  integrations: (client: string) => ['messaging', 'client', client, 'integrations'] as const,
  otp: (client: string) => ['messaging', 'client', client, 'otp'] as const,
  allReports: ['messaging', 'reports'] as const,
};

export const fetchCatalog = () => api.get<ChannelDef[]>('/messaging/providers');

export const fetchVariableCatalog = () => api.get<VariableCatalog>('/messaging/template-variables');

export const fetchClientChannels = (client: string) =>
  api.get<ClientChannel[]>(`/messaging/clients/${client}/channels`);

/** What the service's card says about it, most urgent first. */
export function channelHealth(channel: ClientChannel): {
  tone: 'neutral' | 'danger' | 'success' | 'warning';
  label: string;
} {
  if (channel.status === 'paused') return { tone: 'neutral', label: 'Paused' };
  if (channel.last_error) return { tone: 'danger', label: 'Needs attention' };
  if (channel.verified_at) return { tone: 'success', label: 'Verified' };
  return { tone: 'warning', label: 'Not verified' };
}

export function findProvider(catalog: ChannelDef[] | undefined, id: string) {
  for (const channel of catalog ?? []) {
    const provider = channel.providers.find((p) => p.id === id);
    if (provider) return { channel, provider };
  }
  return null;
}
